<!--
  File name: AdminProfile.vue
  Program Description:
    - This component shows the profile of the logged in user.
    - This component fetches the component that contain all the input fields
      for the general logged in user information.
    - This component allows the user to view and edit the logged in user profile
    - This also includes a way to navigate to the page where the user can reset his
      own password

  Includes:
    - ProfileForm
    - PasswordForm
-->
<template>
  <div class="page">
    <v-row
      justify="center"
    >
      <v-col
        class="pa-0 pb-5"
      >
        <v-card
          tile
          flat
          :loading="$data.$meta.isLoading"
        >
          <v-toolbar
            color="#d0d3d9"
            flat
          >
            <v-toolbar-title
              class="text-h4 font-weight-bold "
            >
              MY PROFILE
            </v-toolbar-title>
            <v-spacer />
            <v-breadcrumbs
              large
              :items="breadcrumbs.items"
              :divider="breadcrumbs.divider"
            />
          </v-toolbar>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="container"
    >
      <v-col>
        <v-navigation-drawer
          width="100%"
          class="rounded"
          style="border-right: 1px solid #cdcdcd"
          :style="`background-color: ${$metaData.app.login.background};`"
        >
          <v-list
            class="pa-0"
          >
            <v-list-item-group>
              <v-list-item
                id="gen-info-tab"
                link
                class="py-1"
                active-class="primary--text"
                @click="switchTab('profile')"
              >
                <v-list-item-action>
                  <v-icon>mdi-cog</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    General Information
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="!isInternal"
                id="reset-pass-tab"
                link
                class="py-1"
                active-class="primary--text"
                @click="switchTab('password')"
              >
                <v-list-item-action>
                  <v-icon>mdi-lock</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    Password
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="!isInternal"
                id="backup-code-tab"
                link
                class="py-1"
                active-class="primary--text"
                @click="switchTab('backupCode')"
              >
                <v-list-item-action>
                  <v-icon>mdi-account-lock</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    Generate Backup Codes
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-navigation-drawer>
      </v-col>
      <v-col
        cols="8"
        class="pa-0"
        align-self="start"
        justify="center"
      >
        <!-- General Information -->
        <template v-if="profileView">
          <ProfileForm
            v-if="profileView"
            ref="profile"
            :fields="$data"
            :profile="true"
            :editable="false"
            :styles="'py-13 px-15 rounded radius shadow'"
          >
            <template v-slot:action>
              <v-btn
                id="cancel-update-btn"
                text
                class=" primary--text font-weight-black mr-3"
                @click=" $data.$meta.readOnly = true, getProfile()"
              >
                Cancel
              </v-btn>
              <v-btn
                v-if="showLabel('canUpdateOwnProfile')"
                id="update-btn"
                style="background-color:#162b54; color: #FFF; "
                text
                type="button"
                :loading="$data.$meta.isLoading"
                @click="confirm('update');"
              >
                {{ getLabel('canUpdateOwnProfile', 'Update') }}
              </v-btn>
            </template>
          </ProfileForm>
        </template>
        <!-- Reset Password -->
        <template v-if="passResetView">
          <v-col class="pa-0">
            <v-card
              tile
              class="py-13 px-15 rounded radius shadow"
            >
              <v-card-title class="pa-0">
                <p
                  class="font-weight-bold primary--text text-h5 "
                >
                  Reset Password
                </p>
                <p
                  v-if="!enableReset"
                  class="text-subtitle-2"
                >
                  <b>
                    It's a good idea to use a strong password that you are not using elsewhere. If you want to reset your password,
                    <span v-if="!isInternal || isAdmin">click the Reset Password button below.</span>
                    <span v-else>request password reset by clicking the button below.</span>
                  </b>
                </p>
              </v-card-title>
              <template v-if="!enableReset">
                <v-card-text class="pa-0">
                  <v-container>
                    <v-img
                      :src="`${$metaData.app.resetPass}`"
                      width="75%"
                      class="ma-auto"
                    />
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    v-if="!isInternal || isAdmin"
                    id="reset-password-btn"
                    color="primary"
                    type="button"
                    block
                    class="py-7 mt-8 rounded-lg"
                    :loading="$data.$meta.isLoading"
                    @click="enableReset = true"
                  >
                    Reset Password
                  </v-btn>
                  <v-btn
                    v-else
                    id="request-reset-btn"
                    color="primary"
                    type="button"
                    block
                    class="py-7 mt-8 rounded-lg"
                    :loading="$data.$meta.isLoading"
                  >
                    Request Password Reset
                  </v-btn>
                </v-card-actions>
              </template>
              <template v-if="enableReset">
                <v-card-text class="pa-0">
                  <PasswordForm
                    ref="passwordForm"
                    v-bind="{ password, confirmPassword, currentPassword }"
                    :is-reset-password="true"
                    old-password
                    :loading="$data.$meta.isLoading"
                    @change="$data[$event.key] = $event.value"
                  />
                  <v-btn
                    id="cancel-reset-btn"
                    text
                    class="primary--text cancel-btn font-weight-black "
                    @click="enableReset = false, clear()"
                  >
                    Cancel
                  </v-btn>
                </v-card-text>
              </template>
            </v-card>
          </v-col>
        </template>
        <!-- Generate Backup Codes -->
        <template v-if="backupCodeView">
          <v-col class="pa-0">
            <v-card
              tile
              class="py-13 px-15 rounded radius shadow"
            >
              <v-card-title class="pa-0">
                <p
                  class="font-weight-bold primary--text text-h5 "
                >
                  Generate New Backup Codes
                </p>
                <p
                  class="text-subtitle-2"
                >
                  <b>
                    If you lost access to your backup codes or you think your backup codes were stolen, create a new set. To create a new set of backup codes, click the button below and we'll send it to you via email.
                  </b>
                </p>
              </v-card-title>

              <v-card-text class="pa-0">
                <v-container>
                  <v-img
                    :src="`${$metaData.app.resetPass}`"
                    width="75%"
                    class="ma-auto"
                  />
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  v-if="!isInternal || isAdmin"
                  id="reset-password-btn"
                  color="primary"
                  type="button"
                  block
                  class="py-7 mt-8 rounded-lg"
                  :disabled="$data.$meta.isLoading"
                  :loading="$data.$meta.isLoading"
                  @click="generateBackupCodes"
                >
                  Generate Codes
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import users from '@/utils/webapi/users'
import adminProfileMixin from '@/mixins/adminProfileMixin'
import { privilegeMixin } from 'session-plugin'
import ProfileForm from '@/components/local/ProfileForm'
import PasswordForm from '@/components/local/PasswordForm'
import { validationMixin, custom, validationErrors } from '@/utils/validations'
import { mapGetters } from 'vuex'
import { errors } from '@/meta'

export default {
  name: 'AdminProfile',
  components: {
    ProfileForm,
    PasswordForm

  },
  mixins: [adminProfileMixin, privilegeMixin, validationMixin],
  data: () => ({
    currentPassword: '',
    password: '',
    confirmPassword: '',
    profileView: true,
    passResetView: false,
    backupCodeView: false,
    enableReset: false,
    npcrsRegistration: ''
  }
  ),
  validations () {
    return {
      password: custom.password,
      confirmPassword: custom.confirmPassword
    }
  },
  computed: {
    ...validationErrors,
    ...mapGetters('lookups', {
      profile: 'getCurrentUser'
    }),
    breadcrumbs () {
      return {
        divider: '>',
        large: true,
        items: [
          {
            text: 'My Profile'
          },
          {
            text: this.profileView ? 'General Information' : this.passResetView ? 'Reset Password - Me' : 'Generate Backup Codes',
            to: {
              name: 'AdminAccount'
            }
          }
        ]
      }
    },
    isInternal () {
      return this.$s.session.userType === 'Internal'
    },
    isAdmin () {
      return this.$s.session.userRole.includes('admin')
    },
    isInvalid () {
      const invalid = this.$v.$invalid
      return invalid
    }
  },
  watch: {
    async email (val) {
      await this.fetchNpcUserData(val)
    }
  },
  created () {
    this.getProfile()
  },
  methods: {
    // assign key and value to this
    handler (key, value) {
      this[`${key}`] = value
    },
    // get profile of current user
    async getProfile () {
      this.getProfileWrap(() => users.profile())
    },
    // generate backup codes for the user
    // backup codes are sent to user's email
    async generateBackupCodes () {
      this.$data.$meta.isLoading = true
      const userEmail = this.profile.email || (await this.$store.dispatch('lookups/getUser')).email
      const resp = await users.generateBackupCodes(userEmail)
      this.$data.$meta.isLoading = false
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: 'Backup codes were generated. Please check your email for the new backup codes.',
          type: 'success'
        })
      } else {
        const actualError = await resp.json()
        const finalError = actualError.error === 'You already reached the maximum limit of requesting backup code for this month.' || actualError.error.includes('Need to wait') ? actualError.error : errors.generic
        this.$store.dispatch('showAlert', {
          message: finalError,
          type: 'error'
        })
      }
    },
    // update user information
    async update () {
      const info = this.updateInfo
      const { userRole, ...data } = info
      await this.updateProfileWrap(() => users.updateProfile(data), info)
      info.userRole = this.$data.userRole.join(', ')
      info.fullName = `${info.firstName}${' ' + info.middleName || ''} ${info.lastName}`
      this.$store.commit('lookups/setCurrentUser', info)
      this.profile._id = info.userId

      if (this.isPic) { this.profile.userRole = 'PIC/PIP' }
      this.updateCrumb()
    },
    // trigger validations of profile form
    confirm (action) {
      this.$refs.profile.$v.$touch()
      if (!this.$refs.profile.$v.$invalid) {
        this.confirmDialog(action)
      } else {
        this.$store.dispatch('showAlert', {
          message: 'Required fields cannot be blank.',
          type: 'error'
        })
      }
    },
    // value changing for tabs
    switchTab (tab) {
      if (tab === 'profile') {
        this.profileView = true
        this.passResetView = false
        this.enableReset = false
        this.backupCodeView = false
      } else if (tab === 'password') {
        this.passResetView = true
        this.profileView = false
        this.backupCodeView = false
      } else if (tab === 'backupCode') {
        this.backupCodeView = true
        this.profileView = false
        this.passResetView = false
        this.enableReset = false
      }
    },
    // clear change password form
    clear () {
      this.currentPassword = ''
      this.password = ''
      this.confirmPassword = ''
      this.$v.$reset()
    },
    async fetchNpcUserData (email) {
      const resp = await users.verifyDpoEmail(email)
      if (resp.ok) {
        this.npcrsRegistration = await resp.json()
      }
    }
  }
}

</script>

<style scoped>
.page {
  /* padding-left: 0; */
  /* box-sizing: border-box; */
  /* height: 100%; */
  display: grid;
  justify-self: center;
  margin: auto;
  max-width:1170px;
}
.shadow {
    box-shadow:4px 8px 8px -2px #c3c3c3 !important;
}
.radius {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
.form >>> .v-card {
  padding: 10% 5% 0% 5%;
  border-radius: 100%;
}
.container {
  padding:1% 0%;
  /* height: 100%; */
  margin-right: 0;
  width: 100%;
}
.btn--fab {
  margin-bottom: 50px;
}
.theme--light.v-text-field.v-input--is-disabled >>> .v-input__slot::before{
  border: none !important;
}

.v-text-field >>> .v-input__slot::after{
  border:none !important;
}

.v-breadcrumbs >>> a{
  color: #162b54 !important;
  font-weight: bolder
}
.v-breadcrumbs >>> {
  font-weight: bolder
}
.cancel-btn{
  position: absolute;
  bottom:52.5px;
  right:220px
}
</style>
